import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Check, Trash2, Upload } from 'lucide-react';
import { Button, IconButton, cn, toast } from '@cyphex/ui';
import {
  BUILTIN_BACKGROUNDS, renderBuiltinBackground,
} from '../../media/video/backgroundCatalog';
import {
  addCustomBackground, loadCustomBackgrounds, removeCustomBackground, type CustomBackground,
} from '../../media/video/backgroundStore';

export interface BackgroundGalleryProps {
  selectedId: string | null;
  onSelect: (backgroundId: string) => void;
}

const THUMB_WIDTH = 160;
const THUMB_HEIGHT = 90;

/**
 * The virtual-background picker. Built-in scenes are drawn on the fly (no
 * bundled photography); uploads are downscaled and kept in this browser only —
 * a background image is never sent anywhere.
 */
export const BackgroundGallery = memo(function BackgroundGallery({
  selectedId, onSelect,
}: BackgroundGalleryProps) {
  const [custom, setCustom] = useState<readonly CustomBackground[]>(loadCustomBackgrounds);
  const fileRef = useRef<HTMLInputElement>(null);

  const builtins = useMemo(
    () =>
      BUILTIN_BACKGROUNDS.map((option) => ({
        ...option,
        src: renderBuiltinBackground(option.id, THUMB_WIDTH, THUMB_HEIGHT)?.toDataURL() ?? '',
      })),
    [],
  );

  const upload = useCallback(async (file: File | undefined): Promise<void> => {
    if (!file) return;
    try {
      const next = await addCustomBackground(file);
      setCustom(next);
      const added = next[0];
      if (added) onSelect(added.id);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'That image could not be used.';
      toast.error(message);
    }
  }, [onSelect]);

  const tiles = [
    ...builtins.map((item) => ({ id: item.id, label: item.label, src: item.src, custom: false })),
    ...custom.map((item) => ({ id: item.id, label: item.label, src: item.dataUrl, custom: true })),
  ];

  return (
    <div className="flex flex-col gap-2">
      <ul className="grid grid-cols-3 gap-2">
        {tiles.map((tile) => (
          <li key={tile.id} className="relative">
            <button
              type="button"
              onClick={() => onSelect(tile.id)}
              aria-pressed={selectedId === tile.id}
              className={cn(
                'block w-full overflow-hidden rounded-lg border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                selectedId === tile.id ? 'border-primary' : 'border-transparent hover:border-border',
              )}
            >
              <img
                src={tile.src}
                alt={`${tile.label} background`}
                className="aspect-video w-full object-cover"
              />
            </button>
            {selectedId === tile.id ? (
              <Check
                size={14}
                strokeWidth={3}
                aria-hidden
                className="absolute left-1.5 top-1.5 rounded-sm bg-primary text-primary-foreground"
              />
            ) : null}
            {tile.custom ? (
              <IconButton
                icon={Trash2}
                label={`Remove ${tile.label}`}
                size="xs"
                className="absolute right-1 top-1"
                onClick={() => setCustom(removeCustomBackground(tile.id))}
              />
            ) : null}
          </li>
        ))}
      </ul>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        aria-label="Upload a background image"
        className="hidden"
        onChange={(event) => {
          void upload(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      <Button
        variant="outline-neutral"
        size="sm"
        leftIcon={Upload}
        onClick={() => fileRef.current?.click()}
      >
        Upload an image
      </Button>
    </div>
  );
});
